<script lang="ts">
  import type { Candle, IndicatorResult } from "../utils/indicators";

  let {
    candles = [],
    indicators = [] as IndicatorResult[],
    width = 800,
    height = 300,
    candleWidth = 6,
    timeFormat,
    placeholder = "Waiting for trades...",
  }: {
    candles: Candle[];
    indicators?: IndicatorResult[];
    width?: number;
    height?: number;
    candleWidth?: number;
    timeFormat?: (ts: number) => string;
    placeholder?: string;
  } = $props();

  const paddingL = 45;
  const paddingR = 5;
  const paddingT = 10;
  const paddingB = 25;
  const CANDLE_GAP = 1;
  const SUB_CHART_H = 80;
  const SUB_CHART_GAP = 8;

  // Hover state
  let hoveredIdx: number | null = $state(null);
  let mouseY = $state(0);
  let tooltipScreenX = $state(0);
  let tooltipScreenY = $state(0);
  let svgEl: SVGSVGElement | undefined = $state(undefined);

  function handleMouseMove(e: MouseEvent) {
    if (!chartData || !svgEl) return;
    const rect = svgEl.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * width;
    const svgYPos = ((e.clientY - rect.top) / rect.height) * totalH;

    const idx = Math.floor((svgX - chartData.offsetX) / candleStep);
    hoveredIdx = idx >= 0 && idx < candles.length ? idx : null;
    mouseY = Math.max(paddingT, Math.min(svgYPos, height - paddingB));

    // Screen-space position for HTML tooltip
    tooltipScreenX = e.clientX - rect.left;
    tooltipScreenY = e.clientY - rect.top;
  }

  function handleMouseLeave() {
    hoveredIdx = null;
  }

  // Hovered candle data
  let hoveredCandle = $derived(
    hoveredIdx !== null && hoveredIdx >= 0 && hoveredIdx < candles.length
      ? candles[hoveredIdx]
      : null,
  );

  // Price at mouseY
  let priceAtMouse = $derived.by(() => {
    if (!chartData || hoveredIdx === null) return null;
    const { minPrice, priceRange } = chartData;
    const fraction = (height - paddingB - mouseY) / plotH;
    return minPrice + fraction * priceRange;
  });

  // Indicator values at hovered index
  let hoveredIndicatorValues = $derived.by(() => {
    if (hoveredIdx === null) return [];
    const vals: { label: string; value: string; color: string }[] = [];
    for (const ind of indicators) {
      if (ind.type === "SMA" || ind.type === "EMA") {
        const pt = ind.points.find((p) => p.index === hoveredIdx);
        if (pt) vals.push({ label: ind.type, value: formatPrice(pt.value), color: ind.color });
      }
      if (ind.type === "BB") {
        const pt = ind.points.find((p) => p.index === hoveredIdx);
        if (pt) {
          vals.push({ label: "BB U", value: formatPrice(pt.upper), color: ind.color });
          vals.push({ label: "BB M", value: formatPrice(pt.middle), color: ind.color });
          vals.push({ label: "BB L", value: formatPrice(pt.lower), color: ind.color });
        }
      }
      if (ind.type === "RSI") {
        const pt = ind.points.find((p) => p.index === hoveredIdx);
        if (pt) vals.push({ label: "RSI", value: pt.value.toFixed(1), color: ind.color });
      }
      if (ind.type === "MACD") {
        const pt = ind.points.find((p) => p.index === hoveredIdx);
        if (pt) {
          vals.push({ label: "MACD", value: pt.macd.toFixed(6), color: ind.color });
          vals.push({ label: "Signal", value: pt.signal.toFixed(6), color: "#f59e0b" });
          vals.push({ label: "Hist", value: pt.histogram.toFixed(6), color: pt.histogram >= 0 ? "#22c55e" : "#ef4444" });
        }
      }
    }
    return vals;
  });

  // Tooltip flip: if hovered candle is in the right half, show tooltip on the left
  let tooltipOnLeft = $derived.by(() => {
    if (!svgEl) return false;
    const rect = svgEl.getBoundingClientRect();
    return tooltipScreenX > rect.width * 0.6;
  });

  // Determine which sub-charts are needed
  let subCharts = $derived.by(() => {
    const subs: { type: "RSI" | "MACD"; color: string; data: any }[] = [];
    for (const ind of indicators) {
      if (ind.type === "RSI" && ind.points.length > 0) {
        subs.push({ type: "RSI", color: ind.color, data: ind.points });
      }
      if (ind.type === "MACD" && ind.points.length > 0) {
        subs.push({ type: "MACD", color: ind.color, data: ind.points });
      }
    }
    return subs;
  });

  let totalH = $derived(height + subCharts.length * (SUB_CHART_H + SUB_CHART_GAP));

  const candleStep = $derived(candleWidth + CANDLE_GAP);
  const plotW = $derived(width - paddingL - paddingR);
  const plotH = $derived(height - paddingT - paddingB);

  const formatPrice = (p: number) => {
    if (p >= 1) return p.toFixed(4);
    if (p >= 0.001) return p.toFixed(6);
    return p.toFixed(9);
  };

  const formatVol = (v: number) => {
    if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return v.toFixed(2);
  };

  const defaultTimeFormat = (ts: number) => {
    const t = new Date(ts);
    return `${t.getHours().toString().padStart(2, "0")}:${t.getMinutes().toString().padStart(2, "0")}:${String(t.getSeconds()).padStart(2, "0")}`;
  };

  const fmt = $derived(timeFormat ?? defaultTimeFormat);

  let chartData = $derived.by(() => {
    if (candles.length < 2) return null;

    let minPrice = Infinity;
    let maxPrice = -Infinity;
    for (const c of candles) {
      if (c.low < minPrice) minPrice = c.low;
      if (c.high > maxPrice) maxPrice = c.high;
    }
    const priceRange = maxPrice - minPrice || 0.000001;

    const scaleY = (price: number) =>
      paddingT + plotH - ((price - minPrice) / priceRange) * plotH;

    const totalCandlesWidth = candles.length * candleStep;
    const offsetX = paddingL + plotW - totalCandlesWidth;

    const bars = candles.map((c, i) => {
      const x = offsetX + i * candleStep;
      const bullish = c.close >= c.open;
      const bodyTop = scaleY(Math.max(c.open, c.close));
      const bodyBottom = scaleY(Math.min(c.open, c.close));
      const bodyH = Math.max(1, bodyBottom - bodyTop);
      const wickTop = scaleY(c.high);
      const wickBottom = scaleY(c.low);
      const color = bullish ? "#22c55e" : "#ef4444";
      return { x, bodyTop, bodyH, wickTop, wickBottom, color, c };
    });

    // Price grid (5 levels)
    const gridLines: { y: number; label: string }[] = [];
    for (let i = 0; i <= 4; i++) {
      const price = minPrice + (priceRange * i) / 4;
      gridLines.push({ y: scaleY(price), label: formatPrice(price) });
    }

    // Time labels (~6 evenly spaced)
    const timeLabels: { x: number; label: string }[] = [];
    const step = Math.max(1, Math.floor(candles.length / 6));
    for (let i = 0; i < candles.length; i += step) {
      const x = offsetX + i * candleStep + candleWidth / 2;
      timeLabels.push({ x, label: fmt(candles[i].timestamp) });
    }

    // Indicator overlay paths (line-type: SMA, EMA, BB)
    const overlayLines: { color: string; points: string }[] = [];
    const overlayBands: { color: string; path: string }[] = [];

    for (const ind of indicators) {
      if (ind.type === "SMA" || ind.type === "EMA") {
        const pts = ind.points
          .filter((p) => p.index >= 0 && p.index < candles.length)
          .map((p) => {
            const x = offsetX + p.index * candleStep + candleWidth / 2;
            const y = scaleY(p.value);
            return `${x},${y}`;
          })
          .join(" ");
        if (pts) overlayLines.push({ color: ind.color, points: pts });
      }

      if (ind.type === "BB" && ind.points.length > 0) {
        const filtered = ind.points.filter(
          (p) => p.index >= 0 && p.index < candles.length,
        );
        if (filtered.length < 2) continue;

        // Middle line
        const middlePts = filtered
          .map((p) => {
            const x = offsetX + p.index * candleStep + candleWidth / 2;
            return `${x},${scaleY(p.middle)}`;
          })
          .join(" ");
        overlayLines.push({ color: ind.color, points: middlePts });

        // Band fill
        const upperPath = filtered.map((p) => {
          const x = offsetX + p.index * candleStep + candleWidth / 2;
          return `${x},${scaleY(p.upper)}`;
        });
        const lowerPath = [...filtered].reverse().map((p) => {
          const x = offsetX + p.index * candleStep + candleWidth / 2;
          return `${x},${scaleY(p.lower)}`;
        });
        const bandPath = `M${upperPath.join(" L")} L${lowerPath.join(" L")}Z`;
        overlayBands.push({ color: ind.color, path: bandPath });
      }
    }

    return {
      bars,
      gridLines,
      timeLabels,
      lastPrice: candles[candles.length - 1].close,
      overlayLines,
      overlayBands,
      scaleY,
      offsetX,
      minPrice,
      maxPrice,
      priceRange,
    };
  });

  // Sub-chart data (RSI, MACD)
  let subChartData = $derived.by(() => {
    if (!chartData) return [];

    return subCharts.map((sub, si) => {
      const topY = height + si * (SUB_CHART_H + SUB_CHART_GAP) + SUB_CHART_GAP;
      const plotArea = { top: topY, height: SUB_CHART_H - 10 };

      if (sub.type === "RSI") {
        const pts = sub.data as { index: number; value: number }[];
        const scaleRSI = (v: number) =>
          plotArea.top + plotArea.height - (v / 100) * plotArea.height;
        const line = pts
          .filter((p: any) => p.index >= 0 && p.index < candles.length)
          .map((p: any) => {
            const x =
              chartData!.offsetX + p.index * candleStep + candleWidth / 2;
            return `${x},${scaleRSI(p.value)}`;
          })
          .join(" ");
        return {
          type: "RSI" as const,
          topY,
          color: sub.color,
          line,
          refLines: [
            { y: scaleRSI(70), label: "70" },
            { y: scaleRSI(30), label: "30" },
            { y: scaleRSI(50), label: "50" },
          ],
        };
      }

      // MACD
      const pts = sub.data as {
        index: number;
        macd: number;
        signal: number;
        histogram: number;
      }[];
      const allVals = pts.flatMap((p) => [
        p.macd,
        p.signal,
        Math.abs(p.histogram),
      ]);
      const maxVal = Math.max(...allVals, 0.000001);
      const minVal = Math.min(...allVals.map((v) => -Math.abs(v)), -0.000001);
      const range = maxVal - minVal || 0.000001;
      const scaleMacd = (v: number) =>
        plotArea.top + plotArea.height - ((v - minVal) / range) * plotArea.height;

      const macdLine = pts
        .filter((p) => p.index >= 0 && p.index < candles.length)
        .map((p) => {
          const x =
            chartData!.offsetX + p.index * candleStep + candleWidth / 2;
          return `${x},${scaleMacd(p.macd)}`;
        })
        .join(" ");

      const signalLine = pts
        .filter((p) => p.index >= 0 && p.index < candles.length)
        .map((p) => {
          const x =
            chartData!.offsetX + p.index * candleStep + candleWidth / 2;
          return `${x},${scaleMacd(p.signal)}`;
        })
        .join(" ");

      const histoBars = pts
        .filter((p) => p.index >= 0 && p.index < candles.length)
        .map((p) => {
          const x = chartData!.offsetX + p.index * candleStep;
          const zeroY = scaleMacd(0);
          const valY = scaleMacd(p.histogram);
          return {
            x,
            y: Math.min(zeroY, valY),
            h: Math.abs(valY - zeroY),
            color: p.histogram >= 0 ? "#22c55e" : "#ef4444",
          };
        });

      return {
        type: "MACD" as const,
        topY,
        color: sub.color,
        macdLine,
        signalLine,
        histoBars,
        zeroY: scaleMacd(0),
      };
    });
  });

  // Crosshair X position (SVG coords)
  let crosshairX = $derived.by(() => {
    if (hoveredIdx === null || !chartData) return 0;
    return chartData.offsetX + hoveredIdx * candleStep + candleWidth / 2;
  });
</script>

<div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-2 relative">
  {#if chartData}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <svg
      bind:this={svgEl}
      viewBox="0 0 {width} {totalH}"
      class="w-full h-auto"
      style="min-height: 250px; cursor: crosshair;"
      onmousemove={handleMouseMove}
      onmouseleave={handleMouseLeave}
    >
      <!-- Grid lines -->
      {#each chartData.gridLines as line}
        <line
          x1={paddingL}
          y1={line.y}
          x2={width - paddingR}
          y2={line.y}
          stroke="#1f2937"
          stroke-width="0.5"
        />
        <text
          x={paddingL - 5}
          y={line.y + 3}
          text-anchor="end"
          fill="#6b7280"
          font-size="8"
          font-family="monospace">{line.label}</text
        >
      {/each}

      <!-- Time labels -->
      {#each chartData.timeLabels as tl}
        <text
          x={tl.x}
          y={height - 5}
          text-anchor="middle"
          fill="#4b5563"
          font-size="7"
          font-family="monospace">{tl.label}</text
        >
      {/each}

      <!-- Bollinger Band fills -->
      {#each chartData.overlayBands as band}
        <path d={band.path} fill={band.color} fill-opacity="0.08" />
      {/each}

      <!-- Candles -->
      {#each chartData.bars as bar, i}
        <!-- Highlight column on hover -->
        {#if hoveredIdx === i}
          <rect
            x={bar.x - 1}
            y={paddingT}
            width={candleWidth + 2}
            height={plotH}
            fill="#ffffff"
            fill-opacity="0.06"
          />
        {/if}
        <line
          x1={bar.x + candleWidth / 2}
          y1={bar.wickTop}
          x2={bar.x + candleWidth / 2}
          y2={bar.wickBottom}
          stroke={bar.color}
          stroke-width="1"
        />
        <rect
          x={bar.x}
          y={bar.bodyTop}
          width={candleWidth}
          height={bar.bodyH}
          fill={bar.color}
          rx="0.5"
        />
      {/each}

      <!-- Indicator overlay lines (SMA, EMA, BB middle) -->
      {#each chartData.overlayLines as overlay}
        <polyline
          points={overlay.points}
          fill="none"
          stroke={overlay.color}
          stroke-width="1.5"
          stroke-opacity="0.8"
        />
      {/each}

      <!-- Crosshair -->
      {#if hoveredIdx !== null && hoveredCandle}
        <!-- Vertical line -->
        <line
          x1={crosshairX}
          y1={paddingT}
          x2={crosshairX}
          y2={height - paddingB}
          stroke="#6b7280"
          stroke-width="0.5"
          stroke-dasharray="3,3"
        />
        <!-- Horizontal line -->
        <line
          x1={paddingL}
          y1={mouseY}
          x2={width - paddingR}
          y2={mouseY}
          stroke="#6b7280"
          stroke-width="0.5"
          stroke-dasharray="3,3"
        />
        <!-- Price label on Y axis -->
        {#if priceAtMouse !== null}
          <rect
            x={0}
            y={mouseY - 6}
            width={paddingL - 2}
            height={12}
            fill="#1f2937"
            rx="1"
          />
          <text
            x={paddingL - 5}
            y={mouseY + 3}
            text-anchor="end"
            fill="#e5e7eb"
            font-size="7"
            font-family="monospace">{formatPrice(priceAtMouse)}</text
          >
        {/if}
        <!-- Time label on X axis -->
        <rect
          x={crosshairX - 25}
          y={height - paddingB + 2}
          width={50}
          height={12}
          fill="#1f2937"
          rx="1"
        />
        <text
          x={crosshairX}
          y={height - paddingB + 11}
          text-anchor="middle"
          fill="#e5e7eb"
          font-size="7"
          font-family="monospace">{fmt(hoveredCandle.timestamp)}</text
        >
      {/if}

      <!-- Sub-charts separator -->
      {#if subChartData.length > 0}
        <line
          x1={paddingL}
          y1={height}
          x2={width - paddingR}
          y2={height}
          stroke="#374151"
          stroke-width="0.5"
        />
      {/if}

      <!-- Sub-charts -->
      {#each subChartData as sub}
        {#if sub.type === "RSI"}
          <text
            x={paddingL + 4}
            y={sub.topY + 10}
            fill={sub.color}
            font-size="8"
            font-family="monospace"
            font-weight="bold">RSI</text
          >
          {#each sub.refLines as ref}
            <line
              x1={paddingL}
              y1={ref.y}
              x2={width - paddingR}
              y2={ref.y}
              stroke="#1f2937"
              stroke-width="0.5"
              stroke-dasharray="3,3"
            />
            <text
              x={paddingL - 5}
              y={ref.y + 3}
              text-anchor="end"
              fill="#4b5563"
              font-size="7"
              font-family="monospace">{ref.label}</text
            >
          {/each}
          {#if sub.line}
            <polyline
              points={sub.line}
              fill="none"
              stroke={sub.color}
              stroke-width="1.5"
              stroke-opacity="0.9"
            />
          {/if}
          <!-- Crosshair vertical through sub-chart -->
          {#if hoveredIdx !== null}
            <line
              x1={crosshairX}
              y1={sub.topY}
              x2={crosshairX}
              y2={sub.topY + SUB_CHART_H - 10}
              stroke="#6b7280"
              stroke-width="0.5"
              stroke-dasharray="3,3"
            />
          {/if}
        {:else if sub.type === "MACD"}
          <text
            x={paddingL + 4}
            y={sub.topY + 10}
            fill={sub.color}
            font-size="8"
            font-family="monospace"
            font-weight="bold">MACD</text
          >
          <line
            x1={paddingL}
            y1={sub.zeroY}
            x2={width - paddingR}
            y2={sub.zeroY}
            stroke="#374151"
            stroke-width="0.5"
            stroke-dasharray="3,3"
          />
          {#each sub.histoBars as bar}
            <rect
              x={bar.x}
              y={bar.y}
              width={candleWidth}
              height={Math.max(0.5, bar.h)}
              fill={bar.color}
              fill-opacity="0.5"
            />
          {/each}
          {#if sub.macdLine}
            <polyline
              points={sub.macdLine}
              fill="none"
              stroke={sub.color}
              stroke-width="1.5"
              stroke-opacity="0.9"
            />
          {/if}
          {#if sub.signalLine}
            <polyline
              points={sub.signalLine}
              fill="none"
              stroke="#f59e0b"
              stroke-width="1"
              stroke-opacity="0.7"
              stroke-dasharray="3,2"
            />
          {/if}
          <!-- Crosshair vertical through sub-chart -->
          {#if hoveredIdx !== null}
            <line
              x1={crosshairX}
              y1={sub.topY}
              x2={crosshairX}
              y2={sub.topY + SUB_CHART_H - 10}
              stroke="#6b7280"
              stroke-width="0.5"
              stroke-dasharray="3,3"
            />
          {/if}
        {/if}
      {/each}
    </svg>

    <!-- HTML Tooltip -->
    {#if hoveredCandle && hoveredIdx !== null}
      {@const bullish = hoveredCandle.close >= hoveredCandle.open}
      {@const ohlcColor = bullish ? "#22c55e" : "#ef4444"}
      <div
        class="absolute pointer-events-none z-10"
        style="top: {tooltipScreenY + 12}px; {tooltipOnLeft ? `right: calc(100% - ${tooltipScreenX - 12}px)` : `left: ${tooltipScreenX + 12}px`};"
      >
        <div class="bg-gray-900 border border-gray-700 rounded-sm px-3 py-2 shadow-lg min-w-[140px]">
          <p class="text-[10px] font-mono text-gray-400 mb-1.5">{fmt(hoveredCandle.timestamp)}</p>
          <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[10px] font-mono">
            <span class="text-gray-500">O</span>
            <span class="text-right tabular-nums" style="color: {ohlcColor}">{formatPrice(hoveredCandle.open)}</span>
            <span class="text-gray-500">H</span>
            <span class="text-right tabular-nums" style="color: {ohlcColor}">{formatPrice(hoveredCandle.high)}</span>
            <span class="text-gray-500">L</span>
            <span class="text-right tabular-nums" style="color: {ohlcColor}">{formatPrice(hoveredCandle.low)}</span>
            <span class="text-gray-500">C</span>
            <span class="text-right tabular-nums" style="color: {ohlcColor}">{formatPrice(hoveredCandle.close)}</span>
            <span class="text-gray-500">Vol</span>
            <span class="text-right tabular-nums text-gray-300">{formatVol(hoveredCandle.volume)}</span>
            <span class="text-gray-500">Trades</span>
            <span class="text-right tabular-nums text-gray-300">{hoveredCandle.trades}</span>
          </div>
          {#if hoveredIndicatorValues.length > 0}
            <div class="border-t border-gray-700/50 mt-1.5 pt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[10px] font-mono">
              {#each hoveredIndicatorValues as iv}
                <span class="tabular-nums" style="color: {iv.color}">{iv.label}</span>
                <span class="text-right tabular-nums text-gray-300">{iv.value}</span>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    {/if}
  {:else}
    <div class="h-64 flex items-center justify-center">
      <p class="text-sm text-gray-600 italic">{placeholder}</p>
    </div>
  {/if}
</div>
